import React from "react";
import SageIcon from "../icons/SageIcon";
import {
  fmtDurationMin,
  formatCardDate,
  formatCardTime,
  hasSageInsights,
  hasUsableSummary,
  hasUsableTranscript,
  meetingWhen,
  participantAvatars,
  participantCount,
  snippetForMeeting,
} from "./meetingHistoryUtils";

const THUMB_TONES = ["violet", "indigo", "slate", "teal"];

function thumbToneFor(item) {
  const key = String(item?.roomName || item?.title || "meeting");
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash + key.charCodeAt(i) * (i + 1)) % THUMB_TONES.length;
  }
  return THUMB_TONES[hash];
}

function MeetingThumbIcon({ size = 40 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      className="mg-hist-card__thumb-svg"
    >
      <rect
        x="4"
        y="10"
        width="28"
        height="22"
        rx="5"
        stroke="currentColor"
        strokeWidth="2.2"
      />
      <path
        d="M32 18.5L42 12.5V29.5L32 23.5V18.5Z"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 3.5h10a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H7l-3 2.2V10.5H3a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LinesIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3.5 4.5h9M3.5 8h9M3.5 11.5h6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="8" cy="3.5" r="1.2" />
      <circle cx="8" cy="8" r="1.2" />
      <circle cx="8" cy="12.5" r="1.2" />
    </svg>
  );
}

export default function MeetingHistoryCard({
  item,
  onOpen,
  layout = "grid",
  variant = "detail",
}) {
  const isRecent = variant === "recent";
  const when = meetingWhen(item);
  const avatars = participantAvatars(item);
  const count = participantCount(item);
  const thumbTone = thumbToneFor(item);
  const files = item.available_files || [];
  const hasRecording = files.includes("recording");
  const hasSummary = hasUsableSummary(item);
  const hasTranscript = hasUsableTranscript(item);
  const sageOn = hasSageInsights(item);
  const showRecorded =
    !isRecent &&
    (hasRecording ||
      item.artifactsStatus === "complete" ||
      item.status === "ended");
  const duration =
    item.totalMeetingSeconds != null &&
    Number.isFinite(Number(item.totalMeetingSeconds)) &&
    Number(item.totalMeetingSeconds) > 0
      ? fmtDurationMin(item.totalMeetingSeconds)
      : null;

  const categoryLabel = item.kind === "instant" ? "Instant" : "Meeting";

  const metaLine = isRecent ? (
    <>
      {when ? formatCardDate(when) : "—"}
      {count > 0 && (
        <>
          <span className="mg-hist-card__bullet">•</span>
          {count} participant{count === 1 ? "" : "s"}
        </>
      )}
    </>
  ) : (
    <>
      {when ? formatCardTime(when) : "—"}
      {count > 0 && (
        <>
          <span className="mg-hist-card__bullet">•</span>
          {count} participant{count === 1 ? "" : "s"}
        </>
      )}
    </>
  );

  return (
    <article
      className={[
        "mg-hist-card",
        `mg-hist-card--${layout}`,
        isRecent ? "mg-hist-card--recent" : "mg-hist-card--detail",
        sageOn ? "has-sage" : "no-sage",
      ].join(" ")}
      onClick={() => onOpen?.(item.roomName)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen?.(item.roomName);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className={`mg-hist-card__thumb mg-hist-card__thumb--${thumbTone}`}>
        <div className="mg-hist-card__thumb-art" aria-hidden>
          <span className="mg-hist-card__thumb-glow" />
          <MeetingThumbIcon size={isRecent ? 38 : 44} />
        </div>
        {showRecorded && (
          <span className="mg-hist-card__badge mg-hist-card__badge--rec">
            <span className="mg-hist-card__badge-dot" />
            Recorded
          </span>
        )}
        {sageOn ? (
          <span className="mg-hist-card__badge mg-hist-card__badge--sage">
            <SageIcon size={12} />
            Sage
          </span>
        ) : (
          <span className="mg-hist-card__badge mg-hist-card__badge--no-sage">
            No Sage
          </span>
        )}
        {duration && <span className="mg-hist-card__dur">{duration}</span>}
      </div>

      <div className="mg-hist-card__body">
        <div className="mg-hist-card__title-row">
          <h3>{item.title || "Untitled meeting"}</h3>
          {/* {!isRecent && (
            <button
              type="button"
              className="mg-hist-card__more"
              aria-label="More options"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreIcon />
            </button>
          )} */}
        </div>

        <p className="mg-hist-card__meta">{metaLine}</p>

        {!isRecent && avatars.length > 0 && (
          <div className="mg-hist-card__avatars">
            {avatars.slice(0, 4).map((person, idx) => (
              <span
                key={person.key}
                className={`mg-hist-avatar mg-hist-avatar--${person.tone}`}
                title={person.name}
                style={idx > 0 ? { marginLeft: -8 } : undefined}
              >
                {person.initials}
              </span>
            ))}
          </div>
        )}

        <p className={`mg-hist-card__snippet${sageOn ? "" : " is-muted-ai"}`}>
          {snippetForMeeting(item)}
        </p>

        {!isRecent && (
          <div className="mg-hist-card__footer">
            <div className="mg-hist-card__tags">
              <span className="mg-hist-tag mg-hist-tag--chip">
                <span className="mg-hist-tag__dot" />
                {categoryLabel}
              </span>
              {sageOn ? (
                <>
                  <span className="mg-hist-tag mg-hist-tag--sage">
                    <SageIcon size={12} />
                    Sage
                  </span>
                  {hasSummary && (
                    <span className="mg-hist-tag mg-hist-tag--soft">
                      <ChatIcon />
                      AI Summary
                    </span>
                  )}
                  {hasTranscript && (
                    <span className="mg-hist-tag mg-hist-tag--soft">
                      <LinesIcon />
                      Transcript
                    </span>
                  )}
                  {!hasSummary && !hasTranscript && (
                    <span className="mg-hist-tag mg-hist-tag--soft">
                      Insights pending
                    </span>
                  )}
                </>
              ) : (
                <span className="mg-hist-tag mg-hist-tag--warn">
                  No AI summary / transcript
                </span>
              )}
            </div>
          </div>
        )}

        {isRecent && (
          <div className="mg-hist-card__recent-flags">
            {sageOn ? (
              <span className="mg-hist-tag mg-hist-tag--sage">
                <SageIcon size={12} />
                Sage insights
              </span>
            ) : (
              <span className="mg-hist-tag mg-hist-tag--warn">
                No AI insights
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
